import { useEffect, useState } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Flame, RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CLASSES = {
  green:  'bg-emerald-500/25 border-emerald-500 text-emerald-100',
  yellow: 'bg-amber-500/25 border-amber-500 text-amber-100',
  red:    'bg-red-500/30 border-red-500 text-red-100',
  idle:   'bg-zinc-700/40 border-zinc-600 text-zinc-300',
};
const STATUS_LABELS = {
  green:  'Finished',
  yellow: 'In-Progress',
  red:    'Behind · Needs Help',
  idle:   'Idle',
};

/**
 * RegionalHeatMap — battlefield oversight for the RM.
 * Toggle between % of jobs completed and % of units picked up.
 */
export function RegionalHeatMap() {
  const [metric, setMetric] = useState('jobs');
  const [data, setData] = useState({ properties: [], summary: {} });
  const [loading, setLoading] = useState(false);

  const load = async (m = metric) => {
    setLoading(true);
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/ops/regional-heatmap?metric=${m}`,
        { credentials: 'include' },
      );
      const d = res.ok ? await res.json() : { properties: [], summary: {} };
      setData(d);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(metric);
    const id = setInterval(() => load(metric), 60_000);
    return () => clearInterval(id);
  }, [metric]);

  const summary = data.summary || {};

  return (
    <Card
      className="bg-zinc-900 border-2 p-4 sm:p-6"
      style={{ borderColor: 'rgba(201, 169, 98, 0.5)' }}
      data-testid="regional-heatmap-card"
    >
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h3 className="font-bold text-lg flex items-center text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
          <Flame className="h-5 w-5 mr-2" style={{ color: '#C9A962' }} />
          REGIONAL HEAT MAP · {data.properties?.length || 0} properties
        </h3>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-md overflow-hidden border border-zinc-700 bg-zinc-800">
            <button
              type="button"
              onClick={() => setMetric('jobs')}
              className={`px-2 py-1 text-[11px] font-semibold ${metric === 'jobs' ? 'bg-[#FFD60A] text-black' : 'text-zinc-300 hover:text-white'}`}
              data-testid="heatmap-metric-jobs"
            >
              Jobs %
            </button>
            <button
              type="button"
              onClick={() => setMetric('units')}
              className={`px-2 py-1 text-[11px] font-semibold ${metric === 'units' ? 'bg-[#FFD60A] text-black' : 'text-zinc-300 hover:text-white'}`}
              data-testid="heatmap-metric-units"
            >
              Units %
            </button>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => load(metric)}
            className="border-zinc-600 text-zinc-300 text-xs"
            data-testid="heatmap-refresh"
          >
            <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary chips */}
      <div className="flex flex-wrap gap-2 mb-3">
        <Badge className="bg-emerald-500/20 text-emerald-300 border border-emerald-500/40" data-testid="heatmap-count-green">
          Finished · {summary.green || 0}
        </Badge>
        <Badge className="bg-amber-500/20 text-amber-300 border border-amber-500/40" data-testid="heatmap-count-yellow">
          In-Progress · {summary.yellow || 0}
        </Badge>
        <Badge className="bg-red-500/25 text-red-300 border border-red-500/40" data-testid="heatmap-count-red">
          Behind · {summary.red || 0}
        </Badge>
        <Badge className="bg-zinc-700/60 text-zinc-300 border border-zinc-600" data-testid="heatmap-count-idle">
          Idle · {summary.idle || 0}
        </Badge>
      </div>

      {(data.properties || []).length === 0 ? (
        <p className="text-xs text-zinc-400">No properties in scope.</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-2">
          {data.properties.map((p) => (
            <div
              key={p.property_id}
              className={`p-2 rounded-md border ${STATUS_CLASSES[p.status] || STATUS_CLASSES.idle}`}
              title={`${p.property_name} — ${p.completed}/${p.total_units} · ${p.percent}%`}
              data-testid={`heatmap-tile-${p.property_id}`}
            >
              <p className="text-xs font-semibold truncate">{p.property_name}</p>
              <p className="text-[10px] opacity-80 truncate">{p.city}</p>
              <div className="flex items-center justify-between mt-1">
                <span className="text-[10px]">{STATUS_LABELS[p.status]}</span>
                <span className="text-[10px] font-bold">{p.percent}%</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

export default RegionalHeatMap;
